import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { qiCreatePrefillValidateSearch } from "@/modules/quotation-invoice/presentation/lib/qi-create-prefill";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const LeaveRequestListPage = lazy(() =>
  import("@/modules/hr/presentation/pages/LeaveRequestListPage").then((m) => ({
    default: m.LeaveRequestListPage,
  })),
);
const LeaveRequestNewPage = lazy(() =>
  import("@/modules/hr/presentation/pages/LeaveRequestNewPage").then((m) => ({
    default: m.LeaveRequestNewPage,
  })),
);
const LeaveRequestDetailPage = lazy(() =>
  import("@/modules/hr/presentation/pages/LeaveRequestDetailPage").then(
    (m) => ({
      default: m.LeaveRequestDetailPage,
    }),
  ),
);
const LeaveRequestEditPage = lazy(() =>
  import("@/modules/hr/presentation/pages/LeaveRequestEditPage").then((m) => ({
    default: m.LeaveRequestEditPage,
  })),
);
const LeaveLineManagerListPage = lazy(() =>
  import("@/modules/hr/presentation/pages/LeaveLineManagerListPage").then(
    (m) => ({ default: m.LeaveLineManagerListPage }),
  ),
);
const LeaveLineManagerDetailPage = lazy(() =>
  import("@/modules/hr/presentation/pages/LeaveLineManagerDetailPage").then(
    (m) => ({ default: m.LeaveLineManagerDetailPage }),
  ),
);
const OvertimeRequestListPage = lazy(() =>
  import("@/modules/hr/presentation/pages/OvertimeRequestListPage").then(
    (m) => ({
      default: m.OvertimeRequestListPage,
    }),
  ),
);
const OvertimeRequestNewPage = lazy(() =>
  import("@/modules/hr/presentation/pages/OvertimeRequestNewPage").then(
    (m) => ({
      default: m.OvertimeRequestNewPage,
    }),
  ),
);
const OvertimeRequestDetailPage = lazy(() =>
  import("@/modules/hr/presentation/pages/OvertimeRequestDetailPage").then(
    (m) => ({
      default: m.OvertimeRequestDetailPage,
    }),
  ),
);
const SalaryAdvanceListPage = lazy(() =>
  import("@/modules/hr/presentation/pages/SalaryAdvanceListPage").then((m) => ({
    default: m.SalaryAdvanceListPage,
  })),
);
const SalaryAdvanceDetailPage = lazy(() =>
  import("@/modules/hr/presentation/pages/SalaryAdvanceDetailPage").then(
    (m) => ({ default: m.SalaryAdvanceDetailPage }),
  ),
);
const SalaryAdvanceNewPage = lazy(() =>
  import("@/modules/hr/presentation/pages/SalaryAdvanceNewPage").then((m) => ({
    default: m.SalaryAdvanceNewPage,
  })),
);
const PayrollHistoryPage = lazy(() =>
  import("@/modules/hr/presentation/pages/PayrollHistoryPage").then((m) => ({
    default: m.PayrollHistoryPage,
  })),
);
const PayrollDetailPage = lazy(() =>
  import("@/modules/hr/presentation/pages/PayrollDetailPage").then((m) => ({
    default: m.PayrollDetailPage,
  })),
);
const PayrollHistoryPreviewPage = lazy(() =>
  import("@/modules/hr/presentation/pages/PayrollHistoryPreviewPage").then(
    (m) => ({
      default: m.PayrollHistoryPreviewPage,
    }),
  ),
);

const MyDocumentsPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/employee/MyDocumentsPage"
  ).then((m) => ({ default: m.MyDocumentsPage })),
);
const MyDocumentCreatePage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/employee/MyDocumentCreatePage"
  ).then((m) => ({ default: m.MyDocumentCreatePage })),
);
const MyDocumentDetailPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/employee/MyDocumentDetailPage"
  ).then((m) => ({ default: m.MyDocumentDetailPage })),
);
const MyDocumentEditPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/employee/MyDocumentEditPage"
  ).then((m) => ({ default: m.MyDocumentEditPage })),
);
const MyDocumentPdfPreviewPage = lazy(() =>
  import(
    "@/modules/quotation-invoice/presentation/pages/employee/MyDocumentPdfPreviewPage"
  ).then((m) => ({ default: m.MyDocumentPdfPreviewPage })),
);

const FormsCenterPage = lazy(() =>
  import("@/modules/document-portal/presentation/pages/FormsCenterPage").then(
    (m) => ({
      default: m.FormsCenterPage,
    }),
  ),
);
const FormDetailPage = lazy(() =>
  import("@/modules/document-portal/presentation/pages/FormDetailPage").then(
    (m) => ({
      default: m.FormDetailPage,
    }),
  ),
);
const MyContractsPage = lazy(() =>
  import("@/modules/document-portal/presentation/pages/MyContractsPage").then(
    (m) => ({
      default: m.MyContractsPage,
    }),
  ),
);
const ContractDetailPage = lazy(() =>
  import(
    "@/modules/document-portal/presentation/pages/ContractDetailPage"
  ).then((m) => ({
    default: m.ContractDetailPage,
  })),
);
const CompanyPoliciesPage = lazy(() =>
  import(
    "@/modules/document-portal/presentation/pages/CompanyPoliciesPage"
  ).then((m) => ({
    default: m.CompanyPoliciesPage,
  })),
);
const PolicyDetailPage = lazy(() =>
  import("@/modules/document-portal/presentation/pages/PolicyDetailPage").then(
    (m) => ({
      default: m.PolicyDetailPage,
    }),
  ),
);
const TemplatesCenterPage = lazy(() =>
  import(
    "@/modules/document-portal/presentation/pages/TemplatesCenterPage"
  ).then((m) => ({
    default: m.TemplatesCenterPage,
  })),
);
const CommunicationMeetingsPage = lazy(() =>
  import(
    "@/modules/communication/presentation/pages/CommunicationMeetingsPage"
  ).then((m) => ({
    default: m.CommunicationMeetingsPage,
  })),
);
const CommunicationMinutesPage = lazy(() =>
  import(
    "@/modules/communication/presentation/pages/CommunicationMinutesPage"
  ).then((m) => ({
    default: m.CommunicationMinutesPage,
  })),
);
const CommunicationCalendarPage = lazy(() =>
  import(
    "@/modules/communication/presentation/pages/CommunicationCalendarPage"
  ).then((m) => ({
    default: m.CommunicationCalendarPage,
  })),
);
const CommunicationNotificationsPage = lazy(() =>
  import(
    "@/modules/communication/presentation/pages/CommunicationNotificationsPage"
  ).then((m) => ({
    default: m.CommunicationNotificationsPage,
  })),
);
const CommunicationMinuteDetailPage = lazy(() =>
  import(
    "@/modules/communication/presentation/pages/CommunicationMinuteDetailPage"
  ).then((m) => ({
    default: m.CommunicationMinuteDetailPage,
  })),
);
const SopListPage = lazy(() =>
  import("@/modules/training-standard/presentation/pages/SopListPage").then(
    (m) => ({
      default: m.SopListPage,
    }),
  ),
);
const SopDetailPage = lazy(() =>
  import("@/modules/training-standard/presentation/pages/SopDetailPage").then(
    (m) => ({
      default: m.SopDetailPage,
    }),
  ),
);
const SopPdfPreviewPage = lazy(() =>
  import(
    "@/modules/training-standard/presentation/pages/SopPdfPreviewPage"
  ).then((m) => ({
    default: m.SopPdfPreviewPage,
  })),
);
const FlowListPage = lazy(() =>
  import("@/modules/training-standard/presentation/pages/FlowListPage").then(
    (m) => ({
      default: m.FlowListPage,
    }),
  ),
);
const FlowDetailPage = lazy(() =>
  import("@/modules/training-standard/presentation/pages/FlowDetailPage").then(
    (m) => ({
      default: m.FlowDetailPage,
    }),
  ),
);
const ServiceStandardListPage = lazy(() =>
  import(
    "@/modules/training-standard/presentation/pages/ServiceStandardListPage"
  ).then((m) => ({
    default: m.ServiceStandardListPage,
  })),
);
const ServiceStandardDetailPage = lazy(() =>
  import(
    "@/modules/training-standard/presentation/pages/ServiceStandardDetailPage"
  ).then((m) => ({
    default: m.ServiceStandardDetailPage,
  })),
);
const ServiceStandardPdfPreviewPage = lazy(() =>
  import(
    "@/modules/training-standard/presentation/pages/ServiceStandardPdfPreviewPage"
  ).then((m) => ({
    default: m.ServiceStandardPdfPreviewPage,
  })),
);
const ChecklistListPage = lazy(() =>
  import(
    "@/modules/training-standard/presentation/pages/ChecklistListPage"
  ).then((m) => ({
    default: m.ChecklistListPage,
  })),
);
const PerformanceKpiPage = lazy(() =>
  import(
    "@/modules/performance-tracking/presentation/pages/PerformanceKpiPage"
  ).then((m) => ({ default: m.PerformanceKpiPage })),
);
const PerformanceKpiDetailPage = lazy(() =>
  import(
    "@/modules/performance-tracking/presentation/pages/PerformanceKpiDetailPage"
  ).then((m) => ({ default: m.PerformanceKpiDetailPage })),
);
const PerformanceTasksPage = lazy(() =>
  import(
    "@/modules/performance-tracking/presentation/pages/PerformanceTasksPage"
  ).then((m) => ({ default: m.PerformanceTasksPage })),
);
const PerformanceProjectMyTasksPage = lazy(() =>
  import(
    "@/modules/performance-tracking/presentation/pages/PerformanceProjectMyTasksPage"
  ).then((m) => ({ default: m.PerformanceProjectMyTasksPage })),
);
const OrgChartOverviewPage = lazy(() =>
  import("@/modules/org-chart/presentation/pages/OrgChartOverviewPage").then(
    (m) => ({ default: m.OrgChartOverviewPage }),
  ),
);
const QcInspectorHubPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QcInspectorHubPage").then((m) => ({
    default: m.QcInspectorHubPage,
  })),
);
const QcInspectionFormPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QcInspectionFormPage").then((m) => ({
    default: m.QcInspectionFormPage,
  })),
);
const QcRecordDetailPage = lazy(() =>
  import("@/modules/qc/presentation/pages/QcRecordDetailPage").then((m) => ({
    default: m.QcRecordDetailPage,
  })),
);
const InternalAuditListPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/InternalAuditListPage"
  ).then((m) => ({
    default: m.InternalAuditListPage,
  })),
);
const InternalAuditRecordFormPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/InternalAuditRecordFormPage"
  ).then((m) => ({
    default: m.InternalAuditRecordFormPage,
  })),
);
const InternalAuditRecordDetailPage = lazy(() =>
  import(
    "@/modules/internal-audit/presentation/pages/InternalAuditRecordDetailPage"
  ).then((m) => ({
    default: m.InternalAuditRecordDetailPage,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/dashboard", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const myDocumentsSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    offset: Math.max(0, Number(search.offset) || 0),
    limit: Math.max(1, Math.min(100, Number(search.limit) || 20)),
    sort: search.sort as OffsetPageQueryDTO["sort"] | undefined,
    filters: search.filters as OffsetPageQueryDTO["filters"] | undefined,
  }),
};

const myDocumentsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "my-documents",
  validateSearch: myDocumentsSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <MyDocumentsPage />
    </LazyPage>
  ),
});
const myDocumentsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "my-documents/new",
  validateSearch: qiCreatePrefillValidateSearch,
  component: () => (
    <LazyPage>
      <MyDocumentCreatePage />
    </LazyPage>
  ),
});
const myDocumentsDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "my-documents/$id",
  component: () => (
    <LazyPage>
      <MyDocumentDetailPage />
    </LazyPage>
  ),
});
const myDocumentsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "my-documents/$id/edit",
  component: () => (
    <LazyPage>
      <MyDocumentEditPage />
    </LazyPage>
  ),
});
const myDocumentsPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "my-documents/$id/pdf-preview",
  validateSearch: (search: Record<string, unknown>) => ({
    print: search.print === 1 || search.print === "1" ? 1 : undefined,
  }),
  component: () => (
    <LazyPage>
      <MyDocumentPdfPreviewPage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

const hrHubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr",
  beforeLoad: () => {
    throw redirect({ to: "/app/hr/leave/requests", replace: true });
  },
  component: () => null,
});
const leaveListSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    sortBy: (search.sortBy as string) || undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
    status: (search.status as string) || undefined,
    offset: Math.max(0, Number(search.offset) || 0),
    limit: Math.max(1, Math.min(100, Number(search.limit) || 20)),
  }),
};

const hrLeaveListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/leave/requests",
  validateSearch: leaveListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <LeaveRequestListPage />
    </LazyPage>
  ),
});
const hrLeaveNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/leave/requests/new",
  component: () => (
    <LazyPage>
      <LeaveRequestNewPage />
    </LazyPage>
  ),
});
const hrLeaveDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/leave/requests/$id",
  component: () => (
    <LazyPage>
      <LeaveRequestDetailPage />
    </LazyPage>
  ),
});
const hrLeaveEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/leave/requests/$id/edit",
  component: () => (
    <LazyPage>
      <LeaveRequestEditPage />
    </LazyPage>
  ),
});
const lineManagerLeaveSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    sortBy: (search.sortBy as string) || undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
    status: (search.status as string) || "pending_line",
    offset: Math.max(0, Number(search.offset) || 0),
    limit: Math.max(1, Math.min(100, Number(search.limit) || 20)),
  }),
};

const hrLeaveLineManagerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/leave/line-manager",
  validateSearch: lineManagerLeaveSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <LeaveLineManagerListPage />
    </LazyPage>
  ),
});

const hrLeaveLineManagerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/leave/line-manager/$id",
  component: () => (
    <LazyPage>
      <LeaveLineManagerDetailPage />
    </LazyPage>
  ),
});

const overtimeListSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    sortBy: (search.sortBy as string) || undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
    status: (search.status as string) || undefined,
    offset: Math.max(0, Number(search.offset) || 0),
    limit: Math.max(1, Math.min(100, Number(search.limit) || 20)),
  }),
};

const hrOvertimeListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/overtime/requests",
  validateSearch: overtimeListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <OvertimeRequestListPage />
    </LazyPage>
  ),
});
const hrOvertimeNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/overtime/requests/new",
  component: () => (
    <LazyPage>
      <OvertimeRequestNewPage />
    </LazyPage>
  ),
});
const hrOvertimeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/overtime/requests/$id",
  component: () => (
    <LazyPage>
      <OvertimeRequestDetailPage />
    </LazyPage>
  ),
});
const hrSalaryAdvanceListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/salary-advance/requests",
  validateSearch: (search: Record<string, unknown>) => ({
    offset: Math.max(0, Number(search.offset) || 0),
    limit: Math.max(1, Math.min(100, Number(search.limit) || 20)),
    status: (search.status as string) || undefined,
  }),
  component: () => (
    <LazyPage>
      <SalaryAdvanceListPage />
    </LazyPage>
  ),
});
const hrSalaryAdvanceNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/salary-advance/requests/new",
  component: () => (
    <LazyPage>
      <SalaryAdvanceNewPage />
    </LazyPage>
  ),
});
const hrSalaryAdvanceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/salary-advance/requests/$id",
  component: () => (
    <LazyPage>
      <SalaryAdvanceDetailPage />
    </LazyPage>
  ),
});
const payrollListSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    offset: Math.max(0, Number(search.offset) || 0),
    limit: Math.max(1, Math.min(100, Number(search.limit) || 10)),
  }),
};

const hrPayrollRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/payroll",
  validateSearch: payrollListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <PayrollHistoryPage />
    </LazyPage>
  ),
});
const hrPayrollDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/payroll/$id",
  component: () => (
    <LazyPage>
      <PayrollDetailPage />
    </LazyPage>
  ),
});
const hrPayrollPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "hr/payroll/preview",
  component: () => (
    <LazyPage>
      <PayrollHistoryPreviewPage />
    </LazyPage>
  ),
});

const documentsFormsSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    category: (search.category as string) || "all",
    search: (search.search as string) || undefined,
    page: Math.max(1, Number(search.page) || 1),
  }),
};

const documentsTemplatesSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    departmentId: (search.departmentId as string) || undefined,
    search: (search.search as string) || undefined,
    page: Math.max(1, Number(search.page) || 1),
  }),
};

const documentsHubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents",
  beforeLoad: () => {
    throw redirect({ to: "/app/documents/forms", replace: true });
  },
  component: () => null,
});
const documentsFormDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/forms/$id",
  component: () => (
    <LazyPage>
      <FormDetailPage />
    </LazyPage>
  ),
});
const documentsFormsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/forms",
  validateSearch: documentsFormsSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <FormsCenterPage />
    </LazyPage>
  ),
});
const documentsTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/templates",
  validateSearch: documentsTemplatesSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <TemplatesCenterPage />
    </LazyPage>
  ),
});
const documentsContractsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/contracts",
  component: () => (
    <LazyPage>
      <MyContractsPage />
    </LazyPage>
  ),
});
const documentsContractDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/contracts/$id",
  component: () => (
    <LazyPage>
      <ContractDetailPage />
    </LazyPage>
  ),
});
const documentsPoliciesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/policies",
  component: () => (
    <LazyPage>
      <CompanyPoliciesPage />
    </LazyPage>
  ),
});
const documentsPolicyDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "documents/policies/$id",
  component: () => (
    <LazyPage>
      <PolicyDetailPage />
    </LazyPage>
  ),
});
const communicationHubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "communication",
  beforeLoad: () => {
    throw redirect({ to: "/app/communication/meetings", replace: true });
  },
  component: () => null,
});
const communicationMeetingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "communication/meetings",
  component: () => (
    <LazyPage>
      <CommunicationMeetingsPage />
    </LazyPage>
  ),
});
const communicationMinutesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "communication/minutes",
  component: () => (
    <LazyPage>
      <CommunicationMinutesPage />
    </LazyPage>
  ),
});
const communicationCalendarSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    tab: search.tab === "list" ? "list" : "calendar",
  }),
};

const communicationCalendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "communication/calendar",
  validateSearch: communicationCalendarSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <CommunicationCalendarPage />
    </LazyPage>
  ),
});
const communicationNotificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "communication/notifications",
  component: () => (
    <LazyPage>
      <CommunicationNotificationsPage />
    </LazyPage>
  ),
});
const communicationMinuteDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "communication/minutes/$id",
  component: () => (
    <LazyPage>
      <CommunicationMinuteDetailPage />
    </LazyPage>
  ),
});
const trainingHubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training",
  beforeLoad: () => {
    throw redirect({ to: "/app/training/sop", replace: true });
  },
  component: () => null,
});
const trainingListSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    sortBy: (search.sortBy as string) || undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
  }),
};
const trainingChecklistsSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    sortBy: (search.sortBy as string) || undefined,
    sortOrder:
      search.sortOrder === "asc" || search.sortOrder === "desc"
        ? search.sortOrder
        : undefined,
    checklistId:
      typeof search.checklistId === "string" && search.checklistId.length > 0
        ? search.checklistId
        : undefined,
  }),
};
const trainingSopRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/sop",
  validateSearch: trainingListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <SopListPage />
    </LazyPage>
  ),
});
const trainingSopDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/sop/$id",
  component: () => (
    <LazyPage>
      <SopDetailPage />
    </LazyPage>
  ),
});
const trainingSopPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/sop/$id/pdf-preview",
  component: () => (
    <LazyPage>
      <SopPdfPreviewPage />
    </LazyPage>
  ),
});
const trainingFlowsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/flows",
  validateSearch: trainingListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <FlowListPage />
    </LazyPage>
  ),
});
const trainingFlowDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/flows/$id",
  component: () => (
    <LazyPage>
      <FlowDetailPage />
    </LazyPage>
  ),
});
const trainingStandardsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/standards",
  validateSearch: trainingListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <ServiceStandardListPage />
    </LazyPage>
  ),
});
const trainingStandardDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/standards/$id",
  component: () => (
    <LazyPage>
      <ServiceStandardDetailPage />
    </LazyPage>
  ),
});
const trainingStandardPdfPreviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/standards/$id/pdf-preview",
  component: () => (
    <LazyPage>
      <ServiceStandardPdfPreviewPage />
    </LazyPage>
  ),
});
const trainingChecklistsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/checklists",
  validateSearch: trainingChecklistsSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <ChecklistListPage />
    </LazyPage>
  ),
});
const trainingChecklistLegacyDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/checklists/$id",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/app/training/checklists",
      search: { checklistId: params.id },
      replace: true,
    });
  },
  component: () => null,
});
const trainingKpiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "training/kpi",
  beforeLoad: () => {
    throw redirect({ to: "/app/performance/kpi", replace: true });
  },
  component: () => null,
});

const performanceHubRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "performance",
  beforeLoad: () => {
    throw redirect({ to: "/app/performance/kpi", replace: true });
  },
  component: () => null,
});
const performanceKpiRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "performance/kpi",
  validateSearch: trainingListSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <PerformanceKpiPage />
    </LazyPage>
  ),
});
const performanceKpiDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "performance/kpi/$assignmentId",
  component: () => (
    <LazyPage>
      <PerformanceKpiDetailPage />
    </LazyPage>
  ),
});
const performanceTasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "performance/tasks",
  component: () => (
    <LazyPage>
      <PerformanceTasksPage />
    </LazyPage>
  ),
});
const performanceProjectMyTasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "performance/projects/$projectId",
  component: () => (
    <LazyPage>
      <PerformanceProjectMyTasksPage />
    </LazyPage>
  ),
});
const orgChartOverviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "org-chart",
  component: () => (
    <LazyPage>
      <OrgChartOverviewPage />
    </LazyPage>
  ),
});
const qcInspectionSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Math.floor(Number(search.page) || 1)),
  }),
};

const internalAuditSearchSchema = {
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Math.floor(Number(search.page) || 1)),
  }),
};

const internalAuditPlanRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "internal-audit/plan/$planId",
  component: () => (
    <LazyPage>
      <InternalAuditRecordFormPage />
    </LazyPage>
  ),
});
const internalAuditRecordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "internal-audit/record/$recordId",
  component: () => (
    <LazyPage>
      <InternalAuditRecordDetailPage />
    </LazyPage>
  ),
});
const internalAuditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "internal-audit",
  validateSearch: internalAuditSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <InternalAuditListPage />
    </LazyPage>
  ),
});

const qcInspectionRecordRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "qc/inspection/record/$recordId",
  component: () => (
    <LazyPage>
      <QcRecordDetailPage />
    </LazyPage>
  ),
});
const qcInspectionStandardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "qc/inspection/$standardId",
  component: () => (
    <LazyPage>
      <QcInspectionFormPage />
    </LazyPage>
  ),
});
const qcInspectionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "qc/inspection",
  validateSearch: qcInspectionSearchSchema.validateSearch,
  component: () => (
    <LazyPage>
      <QcInspectorHubPage />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    dashboardRoute,
    profileRoute,
    myDocumentsListRoute,
    myDocumentsNewRoute,
    myDocumentsDetailRoute,
    myDocumentsEditRoute,
    myDocumentsPdfPreviewRoute,
    hrHubRoute,
    hrLeaveListRoute,
    hrLeaveNewRoute,
    hrLeaveDetailRoute,
    hrLeaveEditRoute,
    hrLeaveLineManagerRoute,
    hrLeaveLineManagerDetailRoute,
    hrOvertimeListRoute,
    hrOvertimeNewRoute,
    hrOvertimeDetailRoute,
    hrSalaryAdvanceListRoute,
    hrSalaryAdvanceNewRoute,
    hrSalaryAdvanceDetailRoute,
    hrPayrollRoute,
    hrPayrollDetailRoute,
    hrPayrollPreviewRoute,
    documentsHubRoute,
    documentsFormDetailRoute,
    documentsFormsRoute,
    documentsTemplatesRoute,
    documentsContractsRoute,
    documentsContractDetailRoute,
    documentsPoliciesRoute,
    documentsPolicyDetailRoute,
    communicationHubRoute,
    communicationMeetingsRoute,
    communicationMinutesRoute,
    communicationCalendarRoute,
    communicationNotificationsRoute,
    communicationMinuteDetailRoute,
    trainingHubRoute,
    trainingSopRoute,
    trainingSopDetailRoute,
    trainingSopPdfPreviewRoute,
    trainingFlowsRoute,
    trainingFlowDetailRoute,
    trainingStandardsRoute,
    trainingStandardDetailRoute,
    trainingStandardPdfPreviewRoute,
    trainingChecklistsRoute,
    trainingChecklistLegacyDetailRoute,
    trainingKpiRoute,
    performanceHubRoute,
    performanceKpiRoute,
    performanceKpiDetailRoute,
    performanceTasksRoute,
    performanceProjectMyTasksRoute,
    orgChartOverviewRoute,
    internalAuditPlanRoute,
    internalAuditRecordRoute,
    internalAuditRoute,
    qcInspectionRecordRoute,
    qcInspectionStandardRoute,
    qcInspectionRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
